import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Modal, Nav, Row, Table } from "react-bootstrap";
import Swal from "sweetalert2";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { toThaiDate } from "../utils/thaiDate";

const STATUS = {
  present: ["มา", "success"],
  late: ["สาย", "warning"],
  absent: ["ขาด", "danger"],
  leave: ["ลา", "info"],
};

const classLabel = (row) => (row.c_class
  ? `${String(row.c_class).replace("ชั้นมัธยมศึกษาปีที่ ", "ม.")}/${row.r_no ?? ""}`
  : "-");

const rateColor = (rate) => (rate == null ? "secondary" : rate >= 90 ? "success" : rate >= 80 ? "warning" : "danger");

const RateBadge = ({ rate }) => (
  <Badge bg={rateColor(rate)}>{rate == null ? "ยังไม่มีข้อมูล" : `${rate}%`}</Badge>
);

// อัตรามาเรียนรายคน — นับ "สาย" เป็นมา ให้ตรงกับตัวเลขรายชุมนุมที่ server คำนวณ
const memberRate = (m) => {
  const total = Number(m.presents || 0) + Number(m.lates || 0) + Number(m.absents || 0) + Number(m.leaves || 0);
  return total ? Math.round(((Number(m.presents || 0) + Number(m.lates || 0)) / total) * 1000) / 10 : null;
};

export default function ClubAttendanceStats({ yearId, year }) {
  const { setLoading } = useLoading();
  const [semester, setSemester] = useState("");
  const [gender, setGender] = useState("");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [club, setClub] = useState(null);

  const load = useCallback(async () => {
    if (!yearId) return;
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`/api/clubs/years/${yearId}/attendance-stats`, {
        params: semester ? { semester } : {}
      });
      setRows(data);
    } catch (error) {
      Swal.fire("ผิดพลาด", error.response?.data?.message || "โหลดสถิติไม่สำเร็จ", "error");
    } finally {
      setLoading(false);
    }
  }, [yearId, semester, setLoading]);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(row => {
    const q = query.trim().toLowerCase();
    return (!gender || row.club_gender === gender)
      && (!q
        || String(row.club_name || "").toLowerCase().includes(q)
        || String(row.club_code || "").toLowerCase().includes(q)
        || String(row.lead_advisor || "").toLowerCase().includes(q));
  });

  const totals = filtered.reduce((acc, row) => ({
    sessions: acc.sessions + row.sessions,
    presents: acc.presents + row.presents,
    lates: acc.lates + row.lates,
    absents: acc.absents + row.absents,
    leaves: acc.leaves + row.leaves,
    marks: acc.marks + row.total_marks,
    missed: acc.missed + (row.rounds_missed > 0 ? 1 : 0),
  }), { sessions: 0, presents: 0, lates: 0, absents: 0, leaves: 0, marks: 0, missed: 0 });
  const overallRate = totals.marks
    ? Math.round(((totals.presents + totals.lates) / totals.marks) * 1000) / 10
    : null;
  const roundsDue = rows[0]?.rounds_due || 0;

  const cards = [
    ["อัตรามาเรียนรวม", overallRate == null ? "-" : `${overallRate}%`, rateColor(overallRate)],
    ["รอบที่ถึงกำหนดแล้ว", roundsDue, "primary"],
    ["ครั้งที่เช็คชื่อแล้ว (รวมทุกชุมนุม)", totals.sessions, "info"],
    ["ชุมนุมที่ยังเช็คไม่ครบ", `${totals.missed} / ${filtered.length}`, totals.missed ? "danger" : "success"],
  ];

  return (
    <>
      <Row className="g-3 mb-3">
        {cards.map(([label, value, color]) => (
          <Col sm={6} lg={3} key={label}>
            <Card className="card-one h-100"><Card.Body>
              <div className={`fs-3 fw-semibold text-${color}`}>{value}</div>
              <div className="text-secondary">{label}</div>
            </Card.Body></Card>
          </Col>
        ))}
      </Row>

      <Card className="card-one">
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div>
            <Card.Title as="h6" className="mb-1">สถิติการเข้าชุมนุม ปีการศึกษา {year?.year_year}</Card.Title>
            <small className="text-secondary">
              มา {totals.presents} · สาย {totals.lates} · ขาด {totals.absents} · ลา {totals.leaves} (อัตรามาเรียนนับสายเป็นมา)
            </small>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Form.Select size="sm" value={semester} onChange={e => setSemester(e.target.value)} style={{ width: 140 }}>
              <option value="">ทั้งปี</option>
              <option value="1">ภาคเรียนที่ 1</option>
              <option value="2">ภาคเรียนที่ 2</option>
            </Form.Select>
            <Form.Select size="sm" value={gender} onChange={e => setGender(e.target.value)} style={{ width: 120 }}>
              <option value="">ทุกเพศ</option>
              <option value="m">ชาย</option>
              <option value="f">หญิง</option>
            </Form.Select>
            <Form.Control size="sm" style={{ width: 220 }} placeholder="ค้นหาชุมนุมหรือครู" value={query} onChange={e => setQuery(e.target.value)} />
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover size="sm" className="mb-0 align-middle">
            <thead>
              <tr>
                <th>ชุมนุม</th><th>เพศ</th><th>ครูหลัก</th>
                <th className="text-center">สมาชิก</th>
                <th className="text-center">เช็คแล้ว/รอบ</th>
                <th className="text-center">มา</th><th className="text-center">สาย</th>
                <th className="text-center">ขาด</th><th className="text-center">ลา</th>
                <th className="text-center">อัตรามาเรียน</th>
                <th>เช็คล่าสุด</th><th />
              </tr>
            </thead>
            <tbody>
              {filtered.map(row => (
                <tr key={row.club_year_id}>
                  <td><div className="fw-semibold">{row.club_name}</div><small className="text-secondary">{row.club_code}</small></td>
                  <td><Badge bg={row.club_gender === "m" ? "primary" : "danger"}>{row.club_gender === "m" ? "ชาย" : "หญิง"}</Badge></td>
                  <td>{row.lead_advisor || "-"}</td>
                  <td className="text-center">{row.enrolled}</td>
                  <td className="text-center">
                    {row.sessions} / {row.rounds_due}
                    {row.rounds_missed > 0 && <div><Badge bg="danger">ยังไม่เช็ค {row.rounds_missed} รอบ</Badge></div>}
                  </td>
                  <td className="text-center text-success">{row.presents}</td>
                  <td className="text-center text-warning">{row.lates}</td>
                  <td className="text-center text-danger">{row.absents}</td>
                  <td className="text-center text-info">{row.leaves}</td>
                  <td className="text-center"><RateBadge rate={row.attendance_rate} /></td>
                  <td>{row.last_session_date ? toThaiDate(row.last_session_date) : "-"}</td>
                  <td className="text-end">
                    <Button size="sm" variant="outline-primary" onClick={() => setClub(row)}>ดูการเช็คชื่อ</Button>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan="12" className="text-center py-4 text-secondary">
                  {rows.length ? "ไม่พบชุมนุมตามเงื่อนไข" : "ยังไม่มีชุมนุมในปีนี้"}
                </td></tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <ClubAttendanceModal club={club} semester={semester} onHide={() => setClub(null)} />
    </>
  );
}

// ใช้ endpoint เดิมของหน้าเช็คชื่อ (ประวัติรายครั้ง / รายชื่อในครั้งนั้น / สมาชิกพร้อมยอดสะสม)
// ผู้จัดการชุมนุม (pos 5/99) ผ่าน canAccessClub อยู่แล้ว ไม่ต้องเปิด endpoint ใหม่
function ClubAttendanceModal({ club, semester, onHide }) {
  const [view, setView] = useState("sessions");
  const [history, setHistory] = useState([]);
  const [members, setMembers] = useState([]);
  const [session, setSession] = useState(null);
  const [sessionRows, setSessionRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!club) return;
    setView("sessions");
    setSession(null);
    setSessionRows([]);
    setBusy(true);
    Promise.all([
      axiosInstance.get(`/api/clubs/offerings/${club.club_year_id}/attendance`),
      axiosInstance.get(`/api/clubs/offerings/${club.club_year_id}/members`),
    ])
      .then(([historyRes, memberRes]) => {
        setHistory(historyRes.data);
        setMembers(memberRes.data);
      })
      .catch(() => Swal.fire("ผิดพลาด", "โหลดการเช็คชื่อไม่สำเร็จ", "error"))
      .finally(() => setBusy(false));
  }, [club]);

  const sessions = useMemo(
    () => history.filter(h => !semester || String(h.semester) === String(semester)),
    [history, semester]
  );

  // ยอดรายคนมาจาก listMembers ซึ่งรวมทั้งปี — ตัวกรองภาคเรียนจึงไม่มีผลกับแท็บนี้
  const memberRows = useMemo(() => members
    .filter(m => m.enrollment_status === "active" || Number(m.total_sessions) > 0)
    .map(m => ({ ...m, rate: memberRate(m) }))
    .sort((a, b) => (a.rate ?? 101) - (b.rate ?? 101)), [members]);

  const openSession = async (row) => {
    setSession(row);
    setSessionRows([]);
    setBusy(true);
    try {
      const { data } = await axiosInstance.get(`/api/clubs/attendance/${row.club_session_id}`);
      setSessionRows(data);
    } catch {
      Swal.fire("ผิดพลาด", "โหลดรายชื่อไม่สำเร็จ", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={!!club} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          การเช็คชื่อ: {club?.club_name}
          <small className="text-secondary ms-2 fs-6">{club?.club_code} · ครูหลัก {club?.lead_advisor || "-"}</small>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Nav variant="pills" activeKey={view} onSelect={k => { setView(k); setSession(null); }} className="mb-3">
          <Nav.Item><Nav.Link eventKey="sessions">รายครั้ง ({sessions.length})</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="members">รายคน ({memberRows.length})</Nav.Link></Nav.Item>
        </Nav>

        {busy && <div className="text-center py-3 text-secondary">กำลังโหลด...</div>}

        {view === "sessions" && !session && !busy && (
          <Table responsive hover size="sm" className="mb-0 align-middle">
            <thead>
              <tr>
                <th>วันที่</th><th>ภาค</th><th>คาบ</th><th>หัวข้อ</th><th>ผู้เช็ค</th>
                <th className="text-center">มา</th><th className="text-center">สาย</th>
                <th className="text-center">ขาด</th><th className="text-center">ลา</th><th />
              </tr>
            </thead>
            <tbody>
              {sessions.map(row => (
                <tr key={row.club_session_id}>
                  <td>{toThaiDate(row.session_date)}</td>
                  <td>{row.semester}</td>
                  <td>{row.period_order}</td>
                  <td>{row.session_topic}</td>
                  <td>{row.checked_by_name}</td>
                  <td className="text-center text-success">{row.presents}</td>
                  <td className="text-center text-warning">{row.lates}</td>
                  <td className="text-center text-danger">{row.absents}</td>
                  <td className="text-center text-info">{row.leaves}</td>
                  <td className="text-end">
                    <Button size="sm" variant="outline-primary" onClick={() => openSession(row)}>ดูรายชื่อ</Button>
                  </td>
                </tr>
              ))}
              {!sessions.length && (
                <tr><td colSpan="10" className="text-center py-4 text-secondary">ยังไม่มีการเช็คชื่อ{semester ? `ในภาคเรียนที่ ${semester}` : ""}</td></tr>
              )}
            </tbody>
          </Table>
        )}

        {view === "sessions" && session && !busy && (
          <>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <div>
                <div className="fw-semibold">
                  {toThaiDate(session.session_date)} · ภาค {session.semester} · คาบ {session.period_order}
                </div>
                <small className="text-secondary">{session.session_topic} · เช็คโดย {session.checked_by_name}</small>
              </div>
              <Button size="sm" variant="outline-secondary" onClick={() => setSession(null)}>
                <i className="ri-arrow-left-line me-1" />กลับ
              </Button>
            </div>
            <Table responsive hover size="sm" className="mb-0 align-middle">
              <thead><tr><th style={{ width: 60 }}>ลำดับ</th><th>เลขประจำตัว</th><th>ชื่อ-สกุล</th><th>ชั้น</th><th>สถานะ</th></tr></thead>
              <tbody>
                {sessionRows.map((row, index) => {
                  const [label, color] = STATUS[row.attendance_status] || [row.attendance_status, "secondary"];
                  return (
                    <tr key={row.club_enrollment_id}>
                      <td>{index + 1}</td>
                      <td>{row.st_idnumber}</td>
                      <td>{row.st_name}</td>
                      <td>{classLabel(row)}</td>
                      <td><Badge bg={color}>{label}</Badge></td>
                    </tr>
                  );
                })}
                {!sessionRows.length && <tr><td colSpan="5" className="text-center py-4 text-secondary">ไม่มีรายชื่อในครั้งนี้</td></tr>}
              </tbody>
            </Table>
          </>
        )}

        {view === "members" && !busy && (
          <>
            <small className="text-secondary d-block mb-2">ยอดสะสมทั้งปี เรียงจากอัตรามาเรียนต่ำสุด</small>
            <Table responsive hover size="sm" className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>เลขประจำตัว</th><th>ชื่อ-สกุล</th><th>ชั้น</th>
                  <th className="text-center">มา</th><th className="text-center">สาย</th>
                  <th className="text-center">ขาด</th><th className="text-center">ลา</th>
                  <th className="text-center">อัตรามาเรียน</th>
                </tr>
              </thead>
              <tbody>
                {memberRows.map(m => (
                  <tr key={m.club_enrollment_id}>
                    <td>{m.st_idnumber}</td>
                    <td>
                      {m.st_name}
                      {m.enrollment_status !== "active" && <Badge bg="secondary" className="ms-1">ออกจากชุมนุมแล้ว</Badge>}
                    </td>
                    <td>{classLabel(m)}</td>
                    <td className="text-center text-success">{m.presents || 0}</td>
                    <td className="text-center text-warning">{m.lates || 0}</td>
                    <td className="text-center text-danger">{m.absents || 0}</td>
                    <td className="text-center text-info">{m.leaves || 0}</td>
                    <td className="text-center"><RateBadge rate={m.rate} /></td>
                  </tr>
                ))}
                {!memberRows.length && <tr><td colSpan="8" className="text-center py-4 text-secondary">ยังไม่มีสมาชิก</td></tr>}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
